import React, { useEffect, useState, useCallback } from "react";
import { Star, StarHalf, StarBorder } from "@mui/icons-material";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import Dialogue from "./Dialogue";

// Function to calculate a contrasting color
export const getContrastingColor = (backgroundColor) => {
  if (typeof backgroundColor === "undefined")
    return "#FFFFFF"; /* KAD added Feb 2, 2025 */
  // Extract RGB values from the color
  const color = backgroundColor.startsWith("#")
    ? backgroundColor.slice(1)
    : backgroundColor;
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Calculate luminance
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Return white for dark backgrounds and black for light backgrounds
  return luminance > 128 ? "#000000" : "#FFFFFF";
};

function decodeUnicodeEscapes(str) {
  return str.replace(/\\u([\dA-Fa-f]{4})/g, (_, g) =>
    String.fromCharCode(parseInt(g, 16))
  );
}

export const TextWithBold = (text, chatbotResponseBubbleColor) => {
  // Calculate the contrast color for backtick and underscore content
  const contrastColor = getContrastingColor(chatbotResponseBubbleColor);

  // Split text and wrap bold, italic, and other formatted parts in appropriate tags
  const renderText = () => {
    // Regex to match **bold**, ***bold***, ****bold****, *****bold*****, ******bold******, *bold grey*, `italic`, and _italic_
    const formatRegex = /(\*{2,6}(.*?)\*{2,6}|\*(.*?)\*|`(.*?)`|_(.*?)_)/g;

    // Regex to match URLs within markdown content
    const urlInMarkdownRegex = /^(https?:\/\/[^\s<>"']+[^\s<>"'.,!?])$/i;

    // Regex to match plain URLs (not already in HTML tags)
    const urlRegex = /(^|\s)(https?:\/\/[^\s<>"']+[^\s<>"'.,!?])/gi;

    // Regex to match image URLs (ending with common image extensions)
    // const imageRegex =
    //   /(^|\s)(https?:\/\/[^\s<>"']+\.(?:png|jpg|jpeg|gif|webp))(?![\w.,!?\/])(?=\s|$|[.,!?])/gi;

    const imageRegex =
      /(^|\s)(https?:\/\/[^\s<>"']+\.(?:png|jpg|jpeg|gif|webp|svg|bmp|tiff|tif|ico|heic|heif)(?:\?[^\s<>"']*)?|data:image\/(?:png|jpeg|gif|webp|svg\+xml);base64,[^\s<>"']+)(?=\s|$|[.,!?;])/gi;

    //const googlePlacesPhotosRegex = /(^|\s)https:\/\/.*XXXX/gi; /* KAD added May 22, 2025 */

    // Step 0: Decode escaped Unicode sequences like \u201c into actual chars
    text = decodeUnicodeEscapes(text);

    // Step 1: Replace existing formatting (**bold**, *grey*, _italic_, `code`)
    let formattedText = text.replace(imageRegex, (match, prefix, url) => {
      console.log("🚀 ~ formattedText ~ match:", match);
      return `${prefix}<img src="${url}" alt="Image" style="max-width: 95%; height: auto; display: block; margin: 10px 0; border-radius: 12px;" />`;
    });
    formattedText = formattedText.replace(
      formatRegex,
      (
        match,
        multiAsteriskContent,
        boldContent,
        singleBoldContent,
        backtickItalicContent,
        underscoreItalicContent
      ) => {
        if (multiAsteriskContent && boldContent) {
          // Check if boldContent is a URL
          if (urlInMarkdownRegex.test(boldContent)) {
            // Wrap URL in <a> tag (optionally inside <strong> for bold styling)
            return `<strong style="color: ${contrastColor};"><a href="${boldContent}" target="_blank" rel="noopener noreferrer" style="color: ${contrastColor}; text-decoration: underline;">${boldContent}</a></strong>`;
          }
          // Wrap non-URL bold content in <strong>
          return `<strong style="color: ${contrastColor};">${boldContent}</strong>`;
        }
        if (singleBoldContent) {
          // Wrap *bold grey* content in <span> with grey color
          return `<span style="color: ${contrastColor}; font-weight: 400;">${singleBoldContent}</span>`;
        }
        if (backtickItalicContent) {
          // Wrap `italic` content in <i> with dynamic color
          return `<i style="font-weight: bold; color: ${contrastColor}; margin-inline: 2px;">${backtickItalicContent}</i>`;
        }
        if (underscoreItalicContent) {
          // Wrap _italic_ content in <i> with dynamic color
          return `<i style="font-weight: bold; color: ${contrastColor}; margin-inline: 2px;">${underscoreItalicContent}</i>`;
        }
        return match; // Return the original match if no condition is met
      }
    );

    // Step 2: Replace image URLs with <img> tags

    // Step 2: Replace Google Photos URLs with <img> tags KAD playing around
    /*
    formattedText = formattedText.replace(googlePlacesPhotosRegex, (match, prefix, url) => {
 console.log("Found Google Match - " + `<img src="${url.replace("XXXX","")}" alt="Image" style="max-width: 95%; height: auto; display: block; margin: 10px 0; border-radius: 12px;" />`);
      return `<img src="${url}" alt="Image" style="max-width: 95%; height: auto; display: block; margin: 10px 0; border-radius: 12px;" />`;
    });
*/

    // Step 3: Replace plain URLs with <a> tags
    formattedText = formattedText.replace(urlRegex, (match, prefix, url) => {
      // Avoid processing URLs that are already part of <img> or <a> tags
      if (
        formattedText.includes(`<img src="${url}"`) ||
        formattedText.includes(`<a href="${url}"`)
      ) {
        return match; // Skip URLs already processed
      }
      return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: ${contrastColor}; text-decoration: underline;">${url}</a>`;
    });

    // Use dangerouslySetInnerHTML to render the HTML content
    return (
      <p
        style={{ margin: 0 }}
        dangerouslySetInnerHTML={{ __html: formattedText }}
      />
    );
  };

  return <div style={{ margin: 0 }}>{renderText()}</div>;
};

const google_map_key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Utility function to transform old schema to new schema format
const transformOldSchema = (items) => {
  if (items.type !== "item_list") return items; // Return unchanged if not old schema

  const transformedData = items.data.map((widget) => {
    if (["menus", "ticket_card", "form"].includes(widget.type)) {
      // Preserve old schema structure for direct rendering
      return widget;
    }
    // Transform for CardPreview compatibility
    return {
      type: widget.type,
      data: widget.data.map((itemArray) =>
        itemArray.map((item) => ({
          id: item.display_name + Math.random(), // Ensure unique ID
          display_type: item.display_type,
          add_display_name: true,
          value: item.value,
          data: {
            elements: [
              { field: "text", value: item.value },
              { field: "display_name", value: item.display_name },
              ...(item.display_type === "map_photo" ||
              item.display_type === "photo"
                ? [
                    {
                      field: "files",
                      value: Array.isArray(item.value)
                        ? item.value.map((url) => ({ url }))
                        : [{ url: item.value }],
                    },
                  ]
                : []),
              ...(item.display_type === "link"
                ? [{ field: "url", value: item.value }]
                : []),
            ],
          },
          css: {
            [item.display_type]: {},
            displayName: {},
            displayNameContainer: {},
          },
        }))
      ),
    };
  });

  return {
    type: "integration_data", // Map to new schema type
    data: transformedData,
  };
};

// Shared styles for consistency
const theme = {
  fonts: {
    primary: "'Google Sans', Roboto, sans-serif",
    size: {
      title: "0.95rem",
      subtitle: "1rem",
      body: "1rem",
    },
  },
  colors: {
    primary: "#007BFF",
    secondary: "#28a745",
    text: "#292929",
    muted: "#555",
    background: "#F5F5F5",
    card: "#DDDDDD",
  },
  spacing: {
    xs: "3px",
    sm: "6px",
    md: "8px",
    lg: "10px",
  },
  borderRadius: "6px",
  shadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
};

/* Vik (Sep 12, 2025): Glassy circular nav buttons + hover (consistent) */
const navBtnBase = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 10,
  height: 36,
  width: 36,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid rgba(255,255,255,0.95)",
  background: "rgba(0,0,0,0.35)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
  backdropFilter: "blur(2px)",
  WebkitBackdropFilter: "blur(2px)",
  color: "#fff",
  fontWeight: 800,
  lineHeight: 1,
  cursor: "pointer",
  userSelect: "none",
  WebkitTapHighlightColor: "transparent",
  transition:
    "transform .15s ease, box-shadow .15s ease, background .15s ease, border-color .15s ease",
};

const navBtnHover = {
  transform: "translateY(-50%) scale(1.06)",
  background: "rgba(0,0,0,0.5)",
  boxShadow: "0 6px 16px rgba(0,0,0,0.6)",
  borderColor: "rgba(255,255,255,1)",
};

// StarRating Component
const StarRating = ({ rating }) => {
  const maxStars = 5;
  const stars = [];

  for (let i = 0; i < maxStars; i++) {
    if (i < Math.floor(rating)) {
      stars.push(<Star key={i} sx={{ color: "#FFD700", fontSize: 18 }} />);
    } else if (i < rating) {
      stars.push(<StarHalf key={i} sx={{ color: "#FFD700", fontSize: 18 }} />);
    } else {
      stars.push(
        <StarBorder key={i} sx={{ color: "#FFD700", fontSize: 18 }} />
      );
    }
  }

  return <Box sx={{ display: "flex", gap: theme.spacing.xs }}>{stars}</Box>;
};

// MenuItem Component
const MenuItem = ({ item }) => {
  console.log("🚀 ~ MenuItem ~ item:", item);

  const { display_name, value, display_type } = item;
  console.log("Value = " + value);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hovered, setHovered] = useState({ prev: false, next: false });
  const buttonStyle = navBtnBase;

  useEffect(() => {
    if (
      display_type === "map_photo" &&
      Array.isArray(value) &&
      value.length > 1
    ) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % value.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [display_type, value]);

  const carouselStyle = {
    width: "100%",
    maxWidth: "300px",
    margin: `${theme.spacing.sm} auto`,
    boxSizing: "border-box",
    // outline: "1px solid red", // Debugging: Remove after testing
  };

  const imageContainerStyle = {
    width: "100%",
    maxWidth: "300px",
    minWidth: "300px",
    height: "140px",
    position: "relative",
    display: "block",
    overflow: "hidden",
    boxSizing: "border-box",
    // outline: "1px solid blue", // Debugging: Remove after testing
  };

  const imageStyle = {
    width: "100%",
    height: "140px",
    objectFit: "cover",
    borderRadius: theme.borderRadius,
    display: "block",
    boxSizing: "border-box",
  };

  // const buttonStyle = {
  //   position: "absolute",
  //   top: "50%",
  //   transform: "translateY(-50%)",
  //   background: "rgba(0, 0, 0, 0.6)",
  //   color: "white",
  //   border: "none",
  //   padding: theme.spacing.xs,
  //   cursor: "pointer",
  //   fontSize: "14px",
  //   borderRadius: "3px",
  //   zIndex: 1,
  // };

  // Preload images to avoid layout shifts
  const preloadImages = Array.isArray(value) ? value : [];
  const getImageUrl = (val) =>
    display_type === "map_photo"
      ? `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photoreference=${val}&key=${google_map_key}`
      : val;

  switch (display_type) {
    case "title":
      return (
        <h3
          style={{
            color: theme.colors.text,
            fontWeight: 600,
            margin: theme.spacing.sm,
            fontFamily: theme.fonts.primary,
            boxSizing: "border-box",
          }}
        >
          {value}
        </h3>
      );
    case "rating":
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            margin: theme.spacing.sm,
            boxSizing: "border-box",
          }}
        >
          <StarRating rating={value} />
        </div>
      );
    case "subtitle":
      return (
        <p
          style={{
            color: theme.colors.muted,
            fontSize: theme.fonts.size.subtitle,
            margin: theme.spacing.xs,
            fontFamily: theme.fonts.primary,
            boxSizing: "border-box",
          }}
        >
          {value}
        </p>
      );
    case "description":
      return (
        <p
          style={{
            color: theme.colors.text,
            fontSize: theme.fonts.size.body,
            margin: theme.spacing.xs,
            fontFamily: theme.fonts.primary,
            boxSizing: "border-box",
          }}
        >
          {value}
        </p>
      );
    case "price":
    case "text":
      return (
        <p
          style={{
            color: theme.colors.primary,
            fontSize: theme.fonts.size.subtitle,
            fontWeight: 500,
            margin: 0,
            fontFamily: theme.fonts.primary,
            boxSizing: "border-box",
            textTransform: "capitalize",
          }}
        >
          {display_name} : {value}
        </p>
      );
    case "link":
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            background: theme.colors.primary,
            color: "white",
            padding: `${theme.spacing.sm}`,
            borderRadius: theme.borderRadius,
            textDecoration: "none",
            display: "inline-block",
            fontSize: theme.fonts.size.body,
            fontFamily: theme.fonts.primary,
            boxSizing: "border-box",
            marginBottom: theme.spacing.sm,
          }}
        >
          {display_name}
        </a>
      );
    case "image":
    case "icon":
      return (
        <img
          loading="lazy"
          src={getImageUrl(value)}
          alt={display_name || "menu item"}
          style={{
            width: "100%",
            height: "100px",
            objectFit: "cover",
            borderRadius: theme.borderRadius,
            margin: theme.spacing.sm,
            boxSizing: "border-box",
            alignSelf: "center",
          }}
        />
      );
    case "map_photo":
    case "photo":
      if (typeof value === "string") {
        const mapImageUrl = getImageUrl(value);

        return (
          <img
            loading="lazy"
            src={mapImageUrl}
            alt={display_name || "menu item"}
            style={{
              width: "100%",
              height: "100px",
              objectFit: "contain",
              borderRadius: theme.borderRadius,
              margin: theme.spacing.sm,
              boxSizing: "border-box",
            }}
          />
        );
      } else if (Array.isArray(value)) {
        const mapImageUrl = getImageUrl(value[currentIndex]);

        return (
          <div style={carouselStyle}>
            {/* Preload images */}
            <div style={{ display: "none" }}>
              {preloadImages.map((val, idx) => (
                <img key={idx} src={getImageUrl(val)} alt="" loading="lazy" />
              ))}
            </div>
            <div style={imageContainerStyle}>
              <button
                style={{
                  ...buttonStyle,
                  left: theme.spacing.xs,
                  ...(hovered.prev ? navBtnHover : null),
                }}
                onMouseEnter={() => setHovered((h) => ({ ...h, prev: true }))}
                onMouseLeave={() => setHovered((h) => ({ ...h, prev: false }))}
                onClick={() =>
                  setCurrentIndex(
                    (currentIndex - 1 + value.length) % value.length
                  )
                }
              >
                <span style={{ textShadow: "0 0 2px #000, 0 0 2px #fff" }}>
                  ❮
                </span>
              </button>

              <img
                loading="lazy"
                src={mapImageUrl}
                alt={display_name || "menu item"}
                style={imageStyle}
              />
              <button
                style={{
                  ...buttonStyle,
                  right: theme.spacing.xs,
                  ...(hovered.next ? navBtnHover : null),
                }}
                onMouseEnter={() => setHovered((h) => ({ ...h, next: true }))}
                onMouseLeave={() => setHovered((h) => ({ ...h, next: false }))}
                onClick={() =>
                  setCurrentIndex((currentIndex + 1) % value.length)
                }
              >
                <span style={{ textShadow: "0 0 2px #000, 0 0 2px #fff" }}>
                  ❯
                </span>
              </button>
            </div>
          </div>
        );
      }
      return null;
    default:
      return null;
  }
};

// Helper function to check if array has isDemo: true
const hasDemo = (array) => array.some((obj) => obj.isDemo === true);

// MenuCard Component
const MenuCard = ({ menu, action_type, sendMessage, onScrollToBottom }) => {
  const handleClick = (item) => {
    if (!item || !Array.isArray(item)) {
      console.warn("Invalid item:", item);
      return;
    }
    const isDemo = hasDemo(item);
    console.log("Item clicked, isDemo:", isDemo);
    if (!isDemo) {
      sendMessage(`I choose this: ${JSON.stringify(item)}`, true);
      onScrollToBottom?.();
    }
  };

  return (
    <div>
      {menu?.map((item, index1) => {
        const latitude = Number(
          item.find((subItem) => subItem?.display_type === "latitude")?.value ||
            0
        );
        const longitude = Number(
          item.find((subItem) => subItem?.display_type === "longitude")
            ?.value || 0
        );

        return (
          <div
            key={index1}
            style={{
              backgroundColor: theme.colors.card,
              borderRadius: theme.borderRadius,
              padding: theme.spacing.md,
              boxShadow: theme.shadow,
              marginBottom: theme.spacing.sm,
              width: "100%",
              maxWidth: "340px !important",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing.xs,
            }}
          >
            {item?.map((item1, index) => (
              <MenuItem key={index} item={item1} />
            ))}

            {latitude && longitude ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: theme.spacing.sm,
                }}
              >
                <Map
                  style={{
                    width: "100%",
                    height: "15vh",
                    borderRadius: "20px",
                  }}
                  defaultCenter={{ lat: latitude, lng: longitude }}
                  defaultZoom={15}
                  gestureHandling={"greedy"}
                  disableDefaultUI={true}
                >
                  <Marker position={{ lat: latitude, lng: longitude }} />
                </Map>
              </div>
            ) : null}

            <Button
              onClick={() => handleClick(item)}
              sx={{
                backgroundColor: theme.colors.primary,
                color: "white",
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                borderRadius: theme.borderRadius,
                fontSize: theme.fonts.size.body,
                fontFamily: theme.fonts.primary,
                textTransform: "none",
                "&:hover": { backgroundColor: "#0056b3" },
                boxSizing: "border-box",
              }}
            >
              Choose
            </Button>
          </div>
        );
      })}
    </div>
  );
};

// EventCard Component
const EventCard = ({ event }) => {
  const eventName =
    event.find((item) => item.display_type === "title")?.value ||
    "Untitled Event";
  const eventImage = event.find(
    (item) => item.display_type === "image" || item.display_type === "photo"
  )?.value;
  const eventDate = event.find((item) => item.display_type === "date")?.value;
  const eventTime = event.find((item) => item.display_type === "time")?.value;
  const eventVenue = event.find(
    (item) => item.display_type === "subtitle"
  )?.value;
  const city = event.find(
    (item) =>
      item.display_type === "location" || item.display_type === "subtitle"
  )?.value;
  const card_body = event.find(
    (item) => item.display_type === "card_body"
  )?.value;
  const ticketUrl = event.find((item) => item.display_type === "link")?.value;
  const latitude = Number(
    event.find((item) => item.display_name === "latitude")?.value || 0
  );
  const longitude = Number(
    event.find((item) => item.display_name === "longitude")?.value || 0
  );

  const openMaps = (lat, lon) => {
    if (lat && lon) {
      window.open(`https://www.google.com/maps?q=${lat},${lon}`, "_blank");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        width: "90%",
        backgroundColor: theme.colors.card,
        borderRadius: theme.borderRadius,
        padding: theme.spacing.sm,
        boxShadow: theme.shadow,
        gap: theme.spacing.sm,
        boxSizing: "border-box",
      }}
    >
      <div style={{ flex: 1, minWidth: "80px" }}>
        {eventImage && (
          <img
            loading="lazy"
            src={Array.isArray(eventImage) ? eventImage[0] : eventImage}
            alt={eventName}
            style={{
              width: "100%",
              height: "60px",
              objectFit: "cover",
              borderRadius: theme.borderRadius,
              boxSizing: "border-box",
            }}
          />
        )}
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.xs,
          boxSizing: "border-box",
        }}
      >
        {eventName && (
          <h3
            style={{
              color: theme.colors.text,
              fontSize: theme.fonts.size.title,
              fontWeight: 600,
              margin: 0,
              fontFamily: theme.fonts.primary,
              boxSizing: "border-box",
            }}
          >
            {eventName}
          </h3>
        )}
        {(eventDate || eventTime) && (
          <p
            style={{
              color: theme.colors.muted,
              fontSize: theme.fonts.size.body,
              margin: 0,
              fontFamily: theme.fonts.primary,
              boxSizing: "border-box",
            }}
          >
            {eventDate} {eventTime ? `| ${eventTime}` : ""}
          </p>
        )}
        {eventVenue && (
          <p
            style={{
              color: theme.colors.muted,
              fontSize: theme.fonts.size.body,
              margin: 0,
              fontFamily: theme.fonts.primary,
              boxSizing: "border-box",
            }}
          >
            {eventVenue}
          </p>
        )}
        {city && (
          <p
            style={{
              color: theme.colors.muted,
              fontSize: theme.fonts.size.body,
              margin: 0,
              fontFamily: theme.fonts.primary,
              boxSizing: "border-box",
            }}
          >
            {city}
          </p>
        )}
        {card_body && (
          <p
            style={{
              color: theme.colors.text,
              fontSize: theme.fonts.size.body,
              margin: 0,
              fontFamily: theme.fonts.primary,
              boxSizing: "border-box",
            }}
          >
            {card_body}
          </p>
        )}
        <div
          style={{
            display: "flex",
            gap: theme.spacing.xs,
            marginTop: theme.spacing.xs,
            boxSizing: "border-box",
          }}
        >
          {ticketUrl && (
            <a
              href={ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                backgroundColor: theme.colors.primary,
                color: "white",
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                borderRadius: theme.borderRadius,
                textDecoration: "none",
                fontSize: theme.fonts.size.body,
                fontFamily: theme.fonts.primary,
                boxSizing: "border-box",
              }}
            >
              Click here
            </a>
          )}
          {latitude && longitude && (
            <button
              onClick={() => openMaps(latitude, longitude)}
              style={{
                backgroundColor: theme.colors.secondary,
                color: "white",
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                borderRadius: theme.borderRadius,
                border: "none",
                cursor: "pointer",
                fontSize: theme.fonts.size.body,
                fontFamily: theme.fonts.primary,
                boxSizing: "border-box",
              }}
            >
              Open in Maps
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// DynamicForm Component
const DynamicForm = ({ schema = [], sendMessage }) => {
  const initialFormData = schema.reduce((acc, field) => {
    if (field.display_type !== "header" && field.display_type !== "button") {
      acc[field.path] = "";
    }
    return acc;
  }, {});

  const [formData, setFormData] = useState(initialFormData);
  const [imagePreview, setImagePreview] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
        setFormData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(JSON.stringify(formData));
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "340px",
        backgroundColor: theme.colors.card,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius,
        boxShadow: theme.shadow,
        boxSizing: "border-box",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.sm,
          boxSizing: "border-box",
        }}
      >
        {schema.map((field) => (
          <div key={field.path}>
            {field.display_type === "header" ? (
              <h2
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  marginBottom: theme.spacing.sm,
                  textAlign: "center",
                  fontFamily: theme.fonts.primary,
                  boxSizing: "border-box",
                }}
              >
                {field.display_name}
              </h2>
            ) : field.display_type === "text" ||
              field.display_type === "number" ? (
              <>
                <label
                  style={{
                    display: "block",
                    fontWeight: 500,
                    marginBottom: theme.spacing.xs,
                    fontSize: theme.fonts.size.subtitle,
                    fontFamily: theme.fonts.primary,
                    boxSizing: "border-box",
                  }}
                >
                  {field.display_name}
                </label>
                <input
                  type={field.display_type}
                  name={field.path}
                  value={formData[field.path]}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: theme.spacing.sm,
                    border: `1px solid ${theme.colors.muted}`,
                    borderRadius: theme.borderRadius,
                    fontSize: theme.fonts.size.body,
                    fontFamily: theme.fonts.primary,
                    backgroundColor: theme.colors.background,
                    boxSizing: "border-box",
                  }}
                />
              </>
            ) : field.display_type === "image" ? (
              <>
                <label
                  style={{
                    display: "block",
                    fontWeight: 500,
                    marginBottom: theme.spacing.xs,
                    fontSize: theme.fonts.size.subtitle,
                    fontFamily: theme.fonts.primary,
                    boxSizing: "border-box",
                  }}
                >
                  {field.display_name}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  required
                  style={{
                    width: "100%",
                    padding: theme.spacing.sm,
                    border: `1px solid ${theme.colors.muted}`,
                    borderRadius: theme.borderRadius,
                    fontSize: theme.fonts.size.body,
                    fontFamily: theme.fonts.primary,
                    backgroundColor: theme.colors.background,
                    boxSizing: "border-box",
                  }}
                />
                {imagePreview && (
                  <img
                    loading="lazy"
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      marginTop: theme.spacing.sm,
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: theme.borderRadius,
                      border: `1px solid ${theme.colors.muted}`,
                      boxSizing: "border-box",
                    }}
                  />
                )}
              </>
            ) : field.display_type === "button" ? (
              <button
                type="submit"
                style={{
                  width: "100%",
                  backgroundColor: theme.colors.primary,
                  color: "white",
                  padding: theme.spacing.sm,
                  fontSize: theme.fonts.size.subtitle,
                  fontWeight: 500,
                  borderRadius: theme.borderRadius,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: theme.fonts.primary,
                  transition: "background 0.3s",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = "#0056b3")
                }
                onMouseOut={(e) =>
                  (e.target.style.backgroundColor = theme.colors.primary)
                }
              >
                {field.display_name}
              </button>
            ) : null}
          </div>
        ))}
      </form>
    </div>
  );
};

export const defaultCss = {
  header: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: "16px 0",
    color: "#333",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },
  paragraph: {
    fontSize: "16px",
    margin: "12px 0",
    color: "#555",
    lineHeight: "1.5",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },
  quote: {
    fontSize: "18px",
    fontStyle: "italic",
    margin: "16px 0",
    padding: "0 20px",
    borderLeft: "4px solid #1976d2",
    color: "#444",
    fontFamily: "Georgia, serif",
    transition: "all 0.3s ease",
  },
  button: {
    fontSize: "16px",
    padding: "8px 16px",
    backgroundColor: "#1976d2",
    color: "#fff",
    borderRadius: "4px",
    fontFamily: "Arial, sans-serif",
    textTransform: "uppercase",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease",
    cursor: "pointer",
  },
  list: {
    fontSize: "16px",
    margin: "12px 0",
    paddingLeft: "20px",
    color: "#555",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },
  image: {
    margin: "16px 0",
    borderRadius: "8px",
    maxWidth: "100%",
    width: "100%",
    height: "auto",
    objectFit: "cover",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
  },
  form: {
    margin: "16px 0",
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #dee2e6",
    fontFamily: "Arial, sans-serif",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
  },
  card: {
    margin: "16px 0",
    padding: "16px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #dee2e6",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },
  map: {
    margin: "16px 0",
    borderRadius: "8px",
    height: "300px",
    width: "100%",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
  },
  table: {
    margin: "16px 0",
    borderCollapse: "collapse",
    width: "100%",
    fontFamily: "Arial, sans-serif",
    border: "1px solid #dee2e6",
    transition: "all 0.3s ease",
  },
  faq: {
    margin: "16px 0",
    padding: "8px 0",
    color: "#555",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },
  displayName: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "8px",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },
  displayNameContainer: {
    padding: "8px",
    backgroundColor: "transparent",
    borderRadius: "4px",
    marginBottom: "8px",
    transition: "all 0.3s ease",
  },
  outer: {
    backgroundColor: "transparent",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    textAlign: "left",
  },
};

export const ImageCarousel = React.memo(
  ({
    images = [],
    caption,
    css,
    index = 0,
    fallbackText = "No images available",
  }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageStatus, setImageStatus] = useState({}); // Track load status and attempts
    const maxRetries = 5; // Maximum number of retries per image
    const retryDelay = 2000; // Delay between retries in milliseconds (1 second)

    const goToPrev = useCallback(() => {
      setCurrentIndex((prev) => {
        let nextIndex = prev - 1;
        // Skip permanently failed images
        while (
          nextIndex >= 0 &&
          imageStatus[images[nextIndex]?.url]?.status === "failed"
        ) {
          nextIndex--;
        }
        return nextIndex < 0 ? images.length - 1 : nextIndex;
      });
    }, [images, imageStatus]);

    const goToNext = useCallback(() => {
      setCurrentIndex((prev) => {
        let nextIndex = prev + 1;
        // Skip permanently failed images
        while (
          nextIndex < images.length &&
          imageStatus[images[nextIndex]?.url]?.status === "failed"
        ) {
          nextIndex++;
        }
        return nextIndex >= images.length ? 0 : nextIndex;
      });
    }, [images, imageStatus]);

    // Preload images with retry logic
    const loadImage = async (url, attempt = 1) => {
      if (
        !url ||
        imageStatus[url]?.status === "loaded" ||
        imageStatus[url]?.status === "failed"
      ) {
        return; // Skip if already processed or permanently failed
      }

      const img = new Image();
      img.src = url;

      img.onload = () => {
        setImageStatus((prev) => ({
          ...prev,
          [url]: { status: "loaded", attempts: attempt },
        }));
      };

      img.onerror = () => {
        if (attempt < maxRetries) {
          // Schedule a retry after a delay
          setTimeout(() => {
            loadImage(url, attempt + 1);
          }, retryDelay * attempt); // Exponential backoff: 1s, 2s, 3s
        } else {
          // Mark as permanently failed after max retries
          setImageStatus((prev) => ({
            ...prev,
            [url]: { status: "failed", attempts: attempt },
          }));
        }
      };
    };

    useEffect(() => {
      images.forEach((image) => loadImage(image.url));
    }, [images, imageStatus]);

    // Auto-scroll to the next image every 3 seconds
    useEffect(() => {
      if (images.length <= 1) return; // Skip if one or no images

      const interval = setInterval(() => {
        goToNext();
      }, 3000 + index * 100); // 3000ms = 3 seconds

      return () => clearInterval(interval);
    }, [goToNext, images.length]);

    // Filter out permanently failed images and limit to 5 images
    const validImages = images
      .filter((image) => imageStatus[image.url]?.status !== "failed")
      .slice(0, 5); // Limit to first 5 images

    if (!validImages.length) {
      return (
        <div
          style={{
            color: "#718096",
            width: "96px",
            height: "96px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            fontSize: "14px",
            fontFamily: "'Arial', sans-serif",
          }}
        >
          {fallbackText}
        </div>
      );
    }

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          margin: css?.margin || "0 0 16px 0",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <img
            loading="lazy"
            src={
              validImages[currentIndex % validImages.length]?.url ||
              "https://via.placeholder.com/400?text=Image+Not+Available"
            }
            alt={`${caption || "Image"} ${currentIndex + 1}`}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "cover",
              maxHeight: "400px",
              borderRadius: "8px",
              ...css,
            }}
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.target.src =
                "https://via.placeholder.com/400?text=Image+Not+Available";
              const url = validImages[currentIndex % validImages.length]?.url;
              if (url && imageStatus[url]?.attempts < maxRetries) {
                // Retry loading the image
                setTimeout(() => {
                  loadImage(url, (imageStatus[url]?.attempts || 0) + 1);
                }, retryDelay * (imageStatus[url]?.attempts || 1));
              } else {
                setImageStatus((prev) => ({
                  ...prev,
                  [url]: { status: "failed", attempts: maxRetries },
                }));
              }
            }}
          />

          {/* Hidden div for preloading images */}
          <div style={{ display: "none" }}>
            {images.map((image, idx) => (
              <img
                key={idx}
                src={image.url}
                alt=""
                loading="lazy"
                onError={() => {
                  if (imageStatus[image.url]?.attempts < maxRetries) {
                    setTimeout(() => {
                      loadImage(
                        image.url,
                        (imageStatus[image.url]?.attempts || 0) + 1
                      );
                    }, retryDelay * (imageStatus[image.url]?.attempts || 1));
                  } else {
                    setImageStatus((prev) => ({
                      ...prev,
                      [image.url]: { status: "failed", attempts: maxRetries },
                    }));
                  }
                }}
                onLoad={() => {
                  setImageStatus((prev) => ({
                    ...prev,
                    [image.url]: {
                      status: "loaded",
                      attempts: imageStatus[image.url]?.attempts || 1,
                    },
                  }));
                }}
              />
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        {validImages.length > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "8px",
            }}
          >
            {validImages.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor:
                    idx === currentIndex % validImages.length
                      ? "#2563eb"
                      : "#d1d5db",
                  cursor: "pointer",
                }}
                onClick={() => setCurrentIndex(idx)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);

export const MapBlockComponent = React.memo(({ lat, lng, css }) => {
  console.log("🚀 ~ MapBlockComponent ~ lat, lng:", lat, lng);
  const [mapComponents, setMapComponents] = useState(null);

  useEffect(() => {
    import("@vis.gl/react-google-maps")
      .then(({ APIProvider, Map, Marker }) =>
        setMapComponents({ APIProvider, Map, Marker })
      )
      .catch((error) => console.error("Error loading Google Maps:", error));
  }, []);

  if (!mapComponents)
    return <div className="p-4 text-gray-600">Loading map...</div>;

  const { APIProvider, Map, Marker } = mapComponents;

  return (
    <div
      style={{
        width: "100%",
        height: "300px",
        borderRadius: "8px",
        overflow: "hidden",
        marginBottom: "16px",
        ...css,
      }}
    >
      <APIProvider apiKey={google_map_key}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={{ lat, lng }}
          defaultZoom={15}
          gestureHandling={"greedy"}
          disableDefaultUI={true}
        >
          <Marker position={{ lat, lng }} />
        </Map>
      </APIProvider>
    </div>
  );
});

export const typeMatches = {
  header: ["heading", "header", "title"],
  paragraph: [
    "text",
    "paragraph",
    "description",
    "subtitle",
    "date",
    "time",
    "price",
    "id",
  ], // Added "id"
  quote: ["quote", "note"],
  button: ["button", "link"],
  list: ["list"],
  image: ["image", "photo", "map_photo"], // Added "photo", "map_photo"
  form: ["form", "signup", "quiz", "feedback", "catering", "contest"],
  map: ["map"],
  table: ["table"],
  faq: ["faq"],
};

export const convertTypeMatches = (inputType) => {
  if (!inputType) return null;
  const normalizedInput = inputType.toLowerCase();
  for (const [blockType, aliases] of Object.entries(typeMatches)) {
    if (aliases.includes(normalizedInput) || blockType === normalizedInput)
      return blockType;
  }
  return null;
};

export const CardPreview = React.memo(
  ({ blocks, onScrollToBottom, sendMessage, isGridUi }) => {
    let parentCss = blocks?.[0]?.parentCss || {};
    const cardStyle = {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      // minHeight: "100%",
      // borderRadius: "20px",
      overflow: "hidden",
      // boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
      // padding: "10px",
      boxSizing: "border-box",

      marginBottom: !isGridUi ? "25px" : 0,
    };

    const chooseThis = (url) => {
      if (!url) {
        sendMessage(`I choose this: ${JSON.stringify(blocks)}`, true);
        onScrollToBottom?.(); // Optional chaining for safer invocation
      }
    };
    const getElementValue = (block, field) => {
      if (field === "display_name") {
        return (
          block?.data?.elements?.find((el) => el.field === field)?.value ||
          block?.display_name
        );
      }
      return (
        block.data?.elements?.find((el) => el.field === field)?.value || null
      );
    };

    const renderBlock = (block) => {
      // console.log("🚀 ~ CardPreview ~ block:", block);
      let outerCss = block?.outerCss;
      const blockType = convertTypeMatches(block?.display_type) || block.type;
      const displayName = block.add_display_name
        ? getElementValue(block, "display_name") || "Unnamed Block"
        : null;
      const isColumnLayout = ["image", "map", "form"].includes(blockType);
      const handleClick = (block) => {
        let url = getElementValue(block, "url");
        if (url) {
          window.open(
            getElementValue(block, "url"),
            "_blank",
            "noopener,noreferrer"
          );
        } else {
          chooseThis(url);
        }
      };
      const blockContent = () => {
        switch (blockType) {
          case "header":
            return (
              <h1
                style={{
                  ...defaultCss.header,
                  ...block.css[blockType],
                  margin: 0,
                }}
                className="text-2xl font-bold"
              >
                {getElementValue(block, "text") || block?.value}
              </h1>
            );
          case "paragraph":
            return (
              <p
                style={{
                  ...defaultCss.paragraph,
                  ...block.css[blockType],
                  margin: 0,
                }}
                className="text-gray-700"
                dangerouslySetInnerHTML={{
                  __html: getElementValue(block, "text") || block?.value,
                }}
              />
            );
          case "list":
            return (
              <ul
                style={{
                  ...defaultCss.list,
                  ...block.css[blockType],
                  margin: 0,
                }}
                className="list-disc pl-5"
              >
                {(getElementValue(block, "items") || []).map((item, i) => (
                  <li key={i} className="text-gray-700">
                    {item}
                  </li>
                ))}
              </ul>
            );
          case "quote":
            return (
              <blockquote
                style={{
                  ...defaultCss.quote,
                  ...block.css[blockType],
                  margin: 0,
                }}
                className="border-l-4 border-gray-300 pl-4 italic text-gray-600"
              >
                {getElementValue(block, "text") || block?.value}
              </blockquote>
            );
          case "button":
            return (
              <Button
                variant="contained"
                style={{
                  marginBottom: "10px",
                  ...defaultCss.button,
                  ...block.css[blockType],
                  width: "90%",
                  margin: 0,
                }}
                onClick={() => handleClick(block)}
                className="bg-blue-500 hover:bg-blue-700 text-white"
              >
                {getElementValue(block, "text") || block?.display_name}
              </Button>
            );
          case "image":
            let urls = getElementValue(block, "files") || [];
            if (!urls.length) {
              const values = Array.isArray(block?.value)
                ? block.value
                : [block?.value];
              urls = values.filter(Boolean).map((url) => ({ url }));
            }
            return (
              <ImageCarousel
                images={urls}
                caption={getElementValue(block, "caption")}
                css={{ ...defaultCss.image, ...block.css[blockType] }}
              />
            );
          case "form":
            return (
              <div
                style={{ ...defaultCss.form, ...block.css[blockType] }}
                className="space-y-4"
              >
                <p className="font-bold">
                  {getElementValue(block, "title") || "Form"}
                </p>
                {(getElementValue(block, "fields") || []).map((field, i) => (
                  <TextField
                    key={i}
                    label={field.label}
                    name={field.name}
                    type={field.field_type || "text"}
                    defaultValue={field.default_value || field.value}
                    placeholder={field.placeholder}
                    required={field.required || false}
                    fullWidth
                    margin="normal"
                    className="bg-white"
                  />
                ))}
                <Button
                  variant="contained"
                  color="primary"
                  className="bg-blue-500 hover:bg-blue-700 text-white"
                >
                  {getElementValue(block, "submitText") || "Submit"}
                </Button>
              </div>
            );
          // case 'card':
          //   return (
          //     <div style={{ ...defaultCss.card, ...block.css[blockType] }} className="p-4 bg-white rounded-lg shadow">
          //       <h3 className="text-lg font-semibold">{getElementValue(block, 'title')}</h3>
          //       <p className="text-gray-700">{getElementValue(block, 'description')}</p>
          //     </div>
          //   );
          case "map":
            if (
              Number(getElementValue(block, "lat")) &&
              getElementValue(block, "lng")
            ) {
              let lat = Number(getElementValue(block, "lat"));
              let long = Number(getElementValue(block, "lng"));
              return (
                <MapBlockComponent
                  lat={lat}
                  lng={long}
                  css={{ ...defaultCss.map, ...block.css[blockType] }}
                />
              );
            } else {
              return null;
            }
          case "table":
            return (
              <div style={{ overflow: "auto", width: "200px" }}>
                <table
                  style={{ ...defaultCss.table, ...block.css[blockType] }}
                  className=" border-collapse"
                >
                  <thead style={{ overflow: "auto", width: "200px" }}>
                    <tr>
                      {(getElementValue(block, "headers") || []).map(
                        (header, i) => (
                          <th
                            key={i}
                            className="border border-gray-300 p-2 bg-gray-100"
                          >
                            {header}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody style={{ overflow: "auto", width: "200px" }}>
                    {(getElementValue(block, "rows") || []).map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j} className="border border-gray-300 p-2">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "faq":
            return (
              <div
                style={{ ...defaultCss.faq, ...block.css[blockType] }}
                className="space-y-2"
              >
                {(getElementValue(block, "items") || []).map((faqItem, i) => (
                  <details key={i} className="bg-white p-3 rounded-lg shadow">
                    <summary className="font-semibold cursor-pointer">
                      {faqItem.question}
                    </summary>
                    <p className="text-gray-700 mt-2">{faqItem.answer}</p>
                  </details>
                ))}
              </div>
            );
          default:
            // return <div style={block.css[blockType]} className="text-red-500">Unsupported block type: {block.type}</div>;
            return null;
        }
      };

      return (
        <div
          style={{
            display: "flex",
            flexDirection: isColumnLayout ? "column" : "row",
            gap: "8px",
            // backgroundColor: "cyan",
          }}
        >
          {displayName && block.add_display_name ? (
            isColumnLayout ? (
              <div style={{ width: "100%", ...outerCss }}>
                <div
                  style={{
                    ...defaultCss.displayNameContainer,
                    ...block.css.displayNameContainer,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      ...defaultCss.displayName,
                      ...block.css.displayName,
                      // paddingInline:"8px"
                    }}
                    className="font-bold text-gray-800"
                  >
                    {displayName}
                  </Typography>
                </div>
                {blockContent()}
              </div>
            ) : (
              <div
                className="flex flex-row items-center justify-start gap-1"
                style={{
                  width: "100%",
                  ...outerCss,
                  paddingInline: "8px",
                  margin: 0,
                }}
              >
                <div
                  style={{
                    ...defaultCss.displayNameContainer,
                    ...block.css.displayNameContainer,
                    margin: 0,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    style={{
                      ...defaultCss.displayName,
                      ...block.css.displayName,
                      // paddingInline:"8px" ,
                      margin: 0,
                    }}
                    className="font-bold text-gray-800 flex-shrink-0"
                  >
                    {displayName}:
                  </Typography>
                </div>
                {blockContent()}
              </div>
            )
          ) : (
            <div style={{ width: "100%", ...outerCss, margin: 0 }}>
              {blockContent()}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="card-preview-container " style={{ ...cardStyle }}>
        <div style={{ ...cardStyle, ...parentCss }}>
          {blocks?.map((block) =>
            // <div
            //   key={block.id}
            //   className="card-block w-full relative"
            //   // style={{ backgroundColor: "red" }}
            //   style={{...parentCss}}
            // >
            //   {renderBlock(block)}
            // </div>
            renderBlock(block)
          )}
        </div>
      </div>
    );
  }
);
// ItemList Component
function removeOuterQuotes(str) {
  if (typeof str !== "string") return str;
  return str.replace(/^"|"$/g, "");
}
const ItemList = ({ items, sendMessage, onScrollToBottom, isMobile }) => {
  useEffect(() => {
    if (onScrollToBottom) {
      onScrollToBottom();
    }
  }, [items, onScrollToBottom]);

  // Transform old schema if necessary
  const processedItems =
    items.type === "item_list" ? transformOldSchema(items) : items;

  return (
    <>
      {processedItems?.type !== "integration_data" ? (
        <Stack width="100%" alignItems={"flex-start"}>
          <Dialogue
            style={{
              background: "#EDEDED",
              borderRadius: "0px 20px 20px 20px",
              padding: "10px 16px",
              gap: "10px",
              maxWidth: "85%",
              color: "#000000",
              fontSize: "16px",
            }}
          >
            <pre
              style={{
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                width: "100%",
                fontWeight: isMobile ? "bolder" : "lighter",
                fontFamily: "inherit",
                margin: 0,
                color: "inherit",
                background: "inherit",
                textAlign: "left",
              }}
            >
              {removeOuterQuotes(
                JSON.stringify(processedItems?.data?.[0]?.data?.[0]?.response)
              )}
            </pre>
          </Dialogue>
        </Stack>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: theme.spacing.sm,
            width: "90%",
            boxSizing: "border-box",
          }}
        >
          <APIProvider apiKey={google_map_key}>
            {processedItems?.data?.map((widget, index) => {
              if (widget?.type === "text") {
                return (
                  <Stack
                    key={index}
                    width="100%"
                    alignItems={"flex-start"}
                    sx={{ marginLeft: -5, marginBlock: "10px" }}
                  >
                    <Dialogue
                      style={{
                        background: "#EDEDED",
                        borderRadius: "0px 20px 20px 20px",
                        padding: "10px 16px",
                        gap: "10px",
                        maxWidth: "85%",
                        color: "#000000",
                        fontSize: "16px",
                      }}
                    >
                      <pre
                        style={{
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                          width: "100%",
                          fontWeight: isMobile ? "bolder" : "lighter",
                          fontFamily: "inherit",
                          margin: 0,
                          color: "inherit",
                          background: "inherit",
                          textAlign: "left",
                        }}
                      >
                        {TextWithBold(
                          removeOuterQuotes(JSON.stringify(widget?.data)),
                          "#EDEDED"
                        )}
                      </pre>
                    </Dialogue>
                  </Stack>
                );
              } else if (widget?.type === "menus") {
                return (
                  <MenuCard
                    key={index}
                    menu={widget?.data}
                    action_type={widget?.action_type}
                    sendMessage={sendMessage}
                    onScrollToBottom={onScrollToBottom}
                  />
                );
              } else if (widget?.type === "ticket_card") {
                return widget?.data?.map((item, i) => (
                  <EventCard key={i} event={item} />
                ));
              } else if (widget?.type === "form") {
                return (
                  <DynamicForm
                    key={index}
                    schema={widget?.data}
                    sendMessage={sendMessage}
                  />
                );
              } else if (widget?.type.toLowerCase() === "itinerary") {
                return (
                  <div key={index} style={{ marginBottom: "16px" }}>
                    {Array.isArray(widget?.data) &&
                      widget?.data?.[0]?.[0]?.type &&
                      widget?.data?.map((wid, idx) => (
                        <ItineraryWidget
                          key={idx}
                          blocks={wid}
                          sendMessage={sendMessage}
                          onScrollToBottom={onScrollToBottom}
                          onPlaceClick={sendMessage}
                          index={idx}
                        />
                      ))}
                  </div>
                );
              } else {
                return (
                  <div
                    key={index}
                    style={
                      widget?.data?.length > 1 &&
                      (widget.type.toLowerCase() === "pizza menu" ||
                        widget.type.toLowerCase() === "google maps")
                        ? {
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "20px",
                            maxWidth: "420px",
                            margin: "0 auto",
                            width: "115%",
                            marginLeft: -20,
                            marginBottom: "16px",
                          }
                        : {
                            display: "flex",
                            justifyContent: "center",
                            maxWidth: "200px",
                            margin: "0 auto",
                            marginBottom: "16px",
                          }
                    }
                  >
                    {Array.isArray(widget?.data) &&
                      widget?.data?.[0]?.[0]?.type &&
                      widget?.data?.map((wid, idx) => (
                        <CardPreview
                          key={idx}
                          blocks={wid}
                          sendMessage={sendMessage}
                          onScrollToBottom={onScrollToBottom}
                          isGridUi={
                            widget.type.toLowerCase() === "pizza menu" ||
                            widget.type.toLowerCase() === "google maps"
                          }
                        />
                      ))}
                  </div>
                );
              }
            })}
          </APIProvider>
        </div>
      )}
    </>
  );
};

export default ItemList;
const cssToStyle = (css) => {
  return Object.entries(css).reduce((acc, [key, value]) => {
    const kebabKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();
    acc[kebabKey] = value;
    return acc;
  }, {});
};

export const ItineraryWidget = ({
  blocks,
  onPlaceClick,
  index,
  sendMessage,
  onScrollToBottom,
}) => {
  console.log("🚀 ~ ItineraryWidget ~ blocks:", blocks);

  // Group blocks into days
  const days = [];
  let currentDay = null;

  blocks.forEach((block) => {
    if (block.type === "header") {
      currentDay = {
        header: block,
        image: null,
        activities: [],
      };
      days.push(currentDay);
    } else if (currentDay) {
      if (block.type === "image") {
        currentDay.image = block;
      } else if (block.type === "paragraph") {
        currentDay.activities.push(block);
      }
    }
  });

  console.log("🚀 ~ ItineraryWidget ~ days:", days);
  const renderTextWithPlaces = (text) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");

    const traverse = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName.toLowerCase() === "place") {
          const placeName = node.textContent;
          return (
            <span
              key={placeName + Math.random()}
              className="itinerary-place"
              onClick={() => {
                if (onPlaceClick) {
                  onPlaceClick(placeName);
                }
              }}
            >
              {placeName}
            </span>
          );
        }

        // Preserve other tags (like <b>, <i>, etc.)
        const Tag = node.tagName.toLowerCase();
        return (
          <Tag key={Math.random()}>
            {Array.from(node.childNodes).map((child, idx) => (
              <React.Fragment key={idx}>{traverse(child)}</React.Fragment>
            ))}
          </Tag>
        );
      }
      return null;
    };

    return Array.from(doc.body.childNodes).map((node, idx) => (
      <React.Fragment key={idx}>{traverse(node)}</React.Fragment>
    ));
  };
  return (
    <div
      style={{
        backgroundColor: "#f9fafb",
        borderRadius: "8px",
        textAlign: "left",
        marginBottom: "16px",
      }}
    >
      <style>
        {`
          .itinerary-day {
            margin-bottom: 16px;
          }
          .itinerary-day-label {
            margin-bottom: 8px;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
          }
          .itinerary-content {
            display: flex;
            align-items: flex-start;
          }
          .itinerary-image-section {
            flex-shrink: 0;
          }
          .itinerary-image-container {
            width: 96px;
            height: 96px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-bottom-left-radius: 8px;
            border-bottom-right-radius: 8px;
            overflow: hidden;
            margin-left: 16px;
            padding-top: 16px;
          }
          .itinerary-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .itinerary-activities {
            flex: 1;
            align-self: center;
          }
          .itinerary-activities ul {
            list-style-type: disc;
            list-style-position: outside;
            margin: 0;
            padding-left: 20px;
            color: #1f2937;
          }
          .itinerary-activities li {
            margin-bottom: 4px;
            line-height: 1.625;
          }
          .itinerary-place {
            color: #2563eb;
            text-decoration: underline;
            cursor: pointer;
          }
          .itinerary-place:hover {
            color: #1e40af;
          }
        `}
      </style>

      {days.map((day, dayIndex) => {
        console.log("🚀 ~ {days.map ~ index:", index, days);
        const headerStyle = cssToStyle(day.header.css.header);
        const imageStyle = day.image ? cssToStyle(day.image.css.image) : {};
        const headerText =
          day.header.data.elements.find((el) => el.field === "text")?.value ||
          `Day ${dayIndex + 1}`;

        return (
          <div key={`${index}-${dayIndex}`} className="itinerary-day">
            {/* Header spanning full width */}
            <div style={headerStyle} className="itinerary-day-label">
              {headerText}
            </div>

            {/* Image and Activities in a flex row below the header */}
            <div className="itinerary-content">
              {/* Image Section */}
              {day.image && (
                <div className="itinerary-image-section">
                  <div className="itinerary-image-container">
                    <ImageCarousel
                      images={
                        day.image.data.elements.find(
                          (el) => el.field === "files"
                        )?.value || []
                      }
                      index={index}
                      css={imageStyle}
                      fallbackText={headerText} // Pass header text as fallback
                    />
                  </div>
                </div>
              )}

              {/* Activities Section */}
              <div
                style={{
                  marginLeft: day.image ? "16px" : "0px",
                  paddingRight: "16px",
                }}
                className="itinerary-activities"
              >
                <ul>
                  {day.activities.map((activity, idx) => {
                    const paragraphStyle = cssToStyle(activity.css.paragraph);
                    const text = activity.data.elements.find(
                      (el) => el.field === "text"
                    )?.value;

                    return (
                      <li
                        key={idx}
                        style={paragraphStyle}
                        // dangerouslySetInnerHTML={{ __html: text }}
                      >
                        {/* {text} */}
                        {renderTextWithPlaces(text)}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
